import EditableList from '@/components/EditableList'
import RelayCard from '@/components/RelayCard'
import { useFetchProfile } from '@/hooks/useFetchProfile'
import { createRelayListDraftEvent } from '@/lib/draft-event'
import { username } from '@/lib/event-metadata'
import { normalizeUrl } from '@/lib/url'
import SecondaryPageLayout from '@/layouts/SecondaryPageLayout'
import { useAtomValue } from 'jotai'
import { invalidateInbox, invalidateRelayList, inboxChannelAtom } from '@/atoms/invalidation'
import client from '@/services/client.service'
import { TMailboxRelay } from '@/types'
import { loadRelayList } from '@nostr/gadgets/lists'
import { unwrappedRelayListAtom } from '@/atoms/lists'
import { forwardRef, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useAsync } from 'react-use'
import { useCurrentPubkey } from '@/atoms/session'

const InboxPage = forwardRef(({ id, index }: { id?: string; index?: number }, ref) => {
  const { t } = useTranslation()
  const currentPubkey = useCurrentPubkey()
  const { profile } = useFetchProfile(id)
  const channel = useAtomValue(inboxChannelAtom)
  const ourRelayList = useAtomValue(unwrappedRelayListAtom)

  useEffect(() => {
    if (!profile) return
    const name = profile.shortName || username(profile) || '...'
    document.title = t("{{name}}'s inbox on {{appName}}", {
      name,
      appName: window.hallway.universe.appName
    })
    history.replaceState(null, '')
  }, [profile])
  const { value: rl } = useAsync(async () => {
    if (!profile) return

    if (profile.pubkey === currentPubkey) return ourRelayList
    return (await loadRelayList(profile.pubkey)).items
  }, [profile?.pubkey, channel, ourRelayList])

  const items = useMemo(() => {
    if (!rl) return []
    return rl
      .filter((r) => r.read)
      .map((r) => ({
        url: r.url,
        scope: (r.read && r.write ? 'both' : 'read') as TMailboxRelay['scope']
      }))
  }, [rl])

  const fullRelays = useMemo(
    () =>
      (rl || []).map((r) => ({
        url: r.url,
        scope: (r.read && r.write ? 'both' : r.read ? 'read' : 'write') as TMailboxRelay['scope']
      })),
    [rl]
  )

  return (
    <SecondaryPageLayout
      ref={ref}
      index={index}
      title={
        profile && profile.pubkey !== currentPubkey
          ? t("{{username}}'s inbox relays", { username: username(profile) })
          : t('Inbox Relays')
      }
    >
      <div className="px-4 py-3">
        <EditableList
          items={items}
          editable={profile?.pubkey === currentPubkey}
          keyExtractor={(r) => r.url}
          renderItem={(relay) => <RelayCard url={relay.url} />}
          validateItem={(url: string) => {
            if (!currentPubkey) return { ok: false as const, error: t('Login required') }
            const normalized = normalizeUrl(url)
            if (!normalized) return { ok: false as const, error: t('Invalid relay URL') }
            if (fullRelays.some((r) => r.url === normalized))
              return { ok: false as const, error: t('Relay already exists') }
            return { ok: true as const, item: { url: normalized, scope: 'read' as const } }
          }}
          onUpdate={async (updated: TMailboxRelay[]) => {
            if (!currentPubkey) return
            const source = rl || []
            const updatedUrls = new Set(updated.map((r) => r.url))
            const full: TMailboxRelay[] = [
              ...source
                .filter((r) => r.write && !updatedUrls.has(r.url))
                .map((r) => ({ url: r.url, scope: 'write' as const })),
              ...updated.map((r) => ({
                url: r.url,
                scope: 'read' as const
              }))
            ]
            const draft = createRelayListDraftEvent(full)
            const event = await client.publish(draft)
            if (!event) {
              console.warn('failed to publish updated relay list event')
              toast.error(t('Failed to publish updated relay list event.'))
              return
            }
            await loadRelayList(event.pubkey, undefined, event)
            invalidateRelayList()
            invalidateInbox()
          }}
          placeholder={t('Enter relay URL')}
          emptyMessage={t('No inbox relays configured.')}
        />
      </div>
    </SecondaryPageLayout>
  )
})
InboxPage.displayName = 'InboxPage'
export default InboxPage
